import { ChevronLeft, ChevronRight, MonitorPlay } from "lucide-react";
import { useState } from "react";
import { setStageView } from "../../lib/api";
import { can } from "../../lib/permissions";
import { captureError } from "../../lib/telemetry";
import { resolveView, viewsFor, type StageViewKey } from "../../lib/stageViews";
import type { Event, EventRole, Question } from "../../lib/types";
import { useStageRemote } from "../../hooks/useStageRemote";
import { useClusters } from "../../hooks/useClusters";

/** "Bühne": the console mirror of the stage view switcher plus Zitate stepping. */
export default function StageRemoteRow({ question, event, role }: { question: Question; event: Event; role: EventRole | null }) {
  const remote = useStageRemote(event.id);
  const [busy, setBusy] = useState(false);
  const { clusters } = useClusters(question.id);
  if (!can(role, "switch_stage_view")) return null;
  const opts = { hasClusters: clusters.length > 0 };
  const views = viewsFor(question, event, opts);
  const active = resolveView(question, event, opts);
  if (views.length <= 1) return null;

  const select = async (key: StageViewKey) => {
    if (busy || key === active.key) return;
    setBusy(true);
    try {
      await setStageView(question.id, key);
    } catch (err) {
      // The stage keeps the previous view; surfacing it here would fight the
      // realtime update that normally repaints this row.
      captureError(err, { where: "StageRemoteRow.setStageView", questionId: question.id, key });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-rc-fog bg-rc-mist/60 px-4 py-3 flex flex-wrap items-center gap-3">
      <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-charcoal">
        <MonitorPlay className="w-3.5 h-3.5" /> Bühne
      </span>
      <div role="radiogroup" aria-label="Ansicht auf der Bühne" className="flex items-center gap-1">
        {views.map((v) => {
          const on = v.key === active.key;
          return (
            <button key={v.key} type="button" role="radio" aria-checked={on} disabled={busy} onClick={() => void select(v.key)}
              className={`inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-[12.5px] font-semibold border transition-colors ${on ? "bg-rc-berry/[0.08] border-rc-berry text-rc-berry" : "bg-rc-paper border-rc-fog text-rc-charcoal hover:text-rc-black"}`}>
              {on && <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-rc-berry animate-pulse" />}
              {v.label}
            </button>
          );
        })}
      </div>
      {active.key === "quotes" && (
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={() => remote.step("prev")} aria-label="Vorheriges Zitat" className="p-2 rounded-full border border-rc-fog bg-rc-paper hover:bg-rc-mist transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" onClick={() => remote.step("next")} aria-label="Nächstes Zitat" className="p-2 rounded-full border border-rc-fog bg-rc-paper hover:bg-rc-mist transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}
